'use client';

import { Box, Skeleton, useMediaQuery } from '@mui/material';
import { theme } from '@repo/ui/Theme';

type CalculatorResultsGraphSkeletonProps = {
  chartHeight?: number;
};

export function CalculatorResultsGraphSkeleton({
  chartHeight = 270,
}: CalculatorResultsGraphSkeletonProps) {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const fillColumnHeight = !isMobile;

  return (
    <Box
      data-testid="calculator-results-graph-skeleton"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: fillColumnHeight ? '100%' : undefined,
        minHeight: chartHeight,
        overflowX: 'hidden',
        width: '100%',
        ...(fillColumnHeight ? { flex: 1, minHeight: chartHeight } : {}),
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flex: fillColumnHeight ? 1 : undefined,
          flexDirection: 'row',
          minHeight: fillColumnHeight ? 0 : chartHeight,
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flexShrink: 0,
            pt: `${20 + 56}px`,
            pb: `${14}px`,
            pl: 0,
            pr: '6px',
            width: 52,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flex: 1,
              flexDirection: 'column',
              justifyContent: 'space-between',
              minHeight: 96,
            }}
          >
            {[36, 44, 40, 38, 32].map((w, i) => (
              <Skeleton key={i} height={12} variant="text" width={w} sx={{ transform: 'none' }} />
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            minHeight: 0,
            minWidth: 0,
            pr: '0px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: { xs: '16px', sm: '32px' },
              justifyContent: 'center',
              pt: '20px',
            }}
          >
            {[0, 1].map(i => (
              <Box
                key={i}
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.75,
                  maxWidth: 172,
                  width: 172,
                }}
              >
                <Skeleton height={28} variant="text" width="78%" sx={{ transform: 'none' }} />
                <Skeleton height={16} variant="text" width="92%" sx={{ transform: 'none' }} />
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              alignItems: 'flex-end',
              display: 'flex',
              flex: 1,
              flexDirection: 'row',
              gap: { xs: '16px', sm: '32px' },
              justifyContent: 'center',
              minHeight: 112,
              mt: 1,
            }}
          >
            <Skeleton
              variant="rounded"
              sx={{
                borderRadius: '8px 8px 0 0',
                height: '76%',
                maxWidth: 172,
                width: 172,
              }}
            />
            <Skeleton
              variant="rounded"
              sx={{
                borderRadius: '8px 8px 0 0',
                height: '52%',
                maxWidth: 172,
                width: 172,
              }}
            />
          </Box>

          <Box sx={{ pb: '14px', pt: 0.5 }}>
            <Box
              sx={{
                borderBottom: '1px dashed #D8D9DA',
                mx: 'auto',
                opacity: 0.95,
                width: '100%',
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
